import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import logo from '../Assets/Logo.png';
import footerImage from '../Assets/template/overview.png';
import { FaAngleDown, FaBars, FaTimes } from 'react-icons/fa';
import LoginModal from './Login2';
import RegisterModal from './Register2';

const Navbar = ({ onSelect, scrollToSection, refs }) => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isActivitiesDropdownOpen, setIsActivitiesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutSectionVisible, setIsAboutSectionVisible] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuRef = useRef(null);

  const toggleLoginModal = () => setIsLoginOpen(!isLoginOpen);
  const toggleRegisterModal = () => setIsRegisterOpen(!isRegisterOpen);

  const toggleAboutDropdown = () => {
    setIsAboutDropdownOpen(!isAboutDropdownOpen);
    setIsActivitiesDropdownOpen(false);
  };

  const toggleActivitiesDropdown = () => {
    setIsActivitiesDropdownOpen(!isActivitiesDropdownOpen);
    setIsAboutDropdownOpen(false);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleScroll = () => {
    if (window.scrollY > 50) setIsScrolled(true);
    else setIsScrolled(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);

    // Set up Intersection Observer for About section
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsAboutSectionVisible(entry.isIntersecting);
      },
      { threshold: 0.1 } // Trigger when 10% of the About section is visible
    );

    if (refs.aboutRef.current) {
      observer.observe(refs.aboutRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
      if (refs.aboutRef.current) {
        observer.unobserve(refs.aboutRef.current);
      }
    };
  }, [refs.aboutRef]);

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setIsAboutDropdownOpen(false);
      setIsActivitiesDropdownOpen(false);
      setIsMobileMenuOpen(false);
    }
  };

  const handleNavClick = (item) => {
    setActiveSection(item);
    onSelect(item);
    setIsMobileMenuOpen(false);
    setIsAboutDropdownOpen(false);
    setIsActivitiesDropdownOpen(false);
  };

  const handleScrollToSection = (ref) => {
    if (ref && ref.current) scrollToSection(ref);
    else scrollToSection(refs.aboutRef);
    setIsMobileMenuOpen(false);
    setIsAboutDropdownOpen(false);
    setIsActivitiesDropdownOpen(false);
  };

  return (
    <>
      <NavbarContainer isScrolled={isScrolled} isAboutSectionVisible={isAboutSectionVisible}>
        <TitleContainer>
          <Logo src={logo} alt="Logo" />
          <Title>NATIONAL IRRIGATION ADMINISTRATION</Title>
        </TitleContainer>

        <HamburgerIcon onClick={toggleMobileMenu}>
          <FaBars size={25} color="#f8fcfa" />
        </HamburgerIcon>

        <NavLinks isMobileMenuOpen={isMobileMenuOpen} ref={menuRef}>
          {isMobileMenuOpen && (
            <CloseButton onClick={toggleMobileMenu}>
              <FaTimes size={25} color="#f8fcfa" />
            </CloseButton>
          )}

          <NavButton onClick={() => handleScrollToSection(refs.heroRef)}>
            <NavLinkText active={activeSection === 'Home'}>Home</NavLinkText>
          </NavButton>

          <DropdownWrapper
            onMouseEnter={() => {
              if (window.innerWidth > 768) setIsAboutDropdownOpen(true);
            }}
            onMouseLeave={() => {
              if (window.innerWidth > 768) setIsAboutDropdownOpen(false);
            }}
          >
            <NavButton onClick={toggleAboutDropdown}>
              <NavLinkText active={activeSection === 'AboutUs'}>About Us <ArrowIcon /></NavLinkText>
            </NavButton>
            {isAboutDropdownOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => handleNavClick('OverviewSection')}>Overview</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('VMOSection')}>Vision, Mission and Objectives</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('OfficesSection')}>Offices</DropdownItem>
              </DropdownMenu>
            )}
          </DropdownWrapper>

          <DropdownWrapper
            onMouseEnter={() => {
              if (window.innerWidth > 768) setIsActivitiesDropdownOpen(true);
            }}
            onMouseLeave={() => {
              if (window.innerWidth > 768) setIsActivitiesDropdownOpen(false);
            }}
          >
            <NavButton onClick={toggleActivitiesDropdown}>
              <NavLinkText active={activeSection === 'Activities'}>Activities <ArrowIcon /></NavLinkText>
            </NavButton>
            {isActivitiesDropdownOpen && (
              <DropdownMenu>
                <DropdownItem onClick={() => handleNavClick('PrepSection')}>Project Preparation</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('WorkshopSection')}>Workshop & Trainings</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('CFPSection')}>Contract Farming Project</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('CollabSection')}>Collaboration & Partnerships</DropdownItem>
                <DropdownItem onClick={() => handleNavClick('SuccessStories')}>Success Stories</DropdownItem>
              </DropdownMenu>
            )}
          </DropdownWrapper>

          <LoginButton onClick={toggleLoginModal}>
            <LoginButtonText>Login</LoginButtonText>
          </LoginButton>
        </NavLinks>
      </NavbarContainer>

      {isMobileMenuOpen && <Overlay onClick={toggleMobileMenu} />}

      <LoginModal
        isOpen={isLoginOpen}
        toggleModal={toggleLoginModal}
        openRegisterModal={() => {
          toggleLoginModal();
          setIsRegisterOpen(true);
        }}
      />

      <RegisterModal
        isOpen={isRegisterOpen}
        toggleModal={toggleRegisterModal}
        openLoginModal={() => {
          toggleRegisterModal();
          setIsLoginOpen(true);
        }}
      />
    </>
  );
};

export default Navbar;

// Styled Components
const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  width: 100%;
  background: ${({ isScrolled, isAboutSectionVisible }) =>
    isAboutSectionVisible
      ? 'linear-gradient(135deg, rgba(24, 53, 46, 0.7) 0%, rgb(34, 139, 34) 100%)'
      : isScrolled
      ? 'rgba(34, 139, 34, 0.95)'
      : 'transparent'};
  box-shadow: ${({ isScrolled }) => (isScrolled ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none')};
  transition: background 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  width: 60px;
  height: 60px;
  margin-right: 15px;
`;

const Title = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: #f4f8f6;
  text-shadow: 0 4px 4px rgba(24, 89, 29, 0.8);
`;

const HamburgerIcon = styled.div`
  display: none;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    width: 300px;
    height: 100%;
    background-color: rgba(24, 111, 24, 0.95);
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    transform: ${({ isMobileMenuOpen }) => (isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)')};
    transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    z-index: 10000;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const NavButton = styled.button`
  background: transparent;
  margin-right: 40px;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;

  &:hover span {
    color: rgb(33, 100, 21);
    transition: color 0.3s ease;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
    margin-bottom: 15px;
    text-align: left;
  }
`;

const NavLinkText = styled.span`
  font-size: 18px;
  color: ${({ active }) => (active ? '#57CE41' : '#f8fcfa')};
  font-weight: 500;
  transition: color 0.3s ease;
`;

const ArrowIcon = styled(FaAngleDown)`
  margin-left: 5px;
  font-size: 16px;
  color: #f8fcfa;
`;

const DropdownWrapper = styled.div`
  position: relative;
  margin-right: 40px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 110%;
  left: 0;
  min-width: 200px;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  overflow: hidden;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    background-color: rgba(255,255,255,0.95);
    width: 100%;
    margin-top: 10px;
  }
`;

const DropdownItem = styled.button`
  background-image: linear-gradient(rgba(34, 139, 34, 0.5), rgba(33, 34, 33, 0.5)), url(${footerImage});
  background-size: cover;
  background-position: center;
  padding: 10px 15px;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-size: 16px;
  color: black;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    background-color: #228B22;
    color: white;
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, rgba(19, 49, 19, 0.8), rgba(23, 43, 20, 0.2));
  padding: 12px 30px;
  border-radius: 25px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    background-color: #228B22;
    color: white;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LoginButtonText = styled.span`
  color: rgb(245, 249, 245);
  font-size: 16px;
  font-weight: 600;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9000;
`;